/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';
import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewCompanyForm from './AddNewCompanyForm';
import AddNewCompanySuccess from './AddNewCompanySuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type ForceMode = 'asset' | 'rush';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
  accountStore: any,
  forceMode?: ForceMode,
  requireLocationType?: boolean,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore', 'accountStore')
@observer
export default class AddNewCompany extends React.Component<*> {
  _modeFromSearchType(searchType: ?string): 'asset' | 'rush' | null {
    if (searchType === 'asset-request') return 'asset';
    if (searchType === 'rush-asset-request') return 'rush';
    return null;
  }

  createForm = () => {
    const { t }: TranslatorProps = this.props;

    const { requireLocationType = true } = this.props;

    // Infer mode from search type or forced prop
    const searchType = this.props.accountStore?.searchType;
    const inferredMode = this._modeFromSearchType(searchType);
    const mode: 'asset' | 'rush' | null = this.props.forceMode || inferredMode;

    // Location type is only required for asset/rush request flows
    const isLocationTypeRequired: boolean =
      requireLocationType && (mode === 'asset' || mode === 'rush');

    // Add * only when the field is actually required in this context
    const locationTypeLabel = isLocationTypeRequired
      ? `${t('general:locations.labels.type')} *`
      : t('general:locations.labels.type');

    const fields: Array<Object> = [
      {
        name: 'addCompanyForm',
        label: t('general:actions.new_company'),
        title: 'New Company',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: true,
            value: this.props.params.id,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            value: '',
          },
        ],
      },
      {
        name: 'locationRequestFormType',
        label: locationTypeLabel,
        type: 'radio',
        fields: [
          {
            name: 'list',
            label: locationTypeLabel,
            // When required, start empty to force user to choose
            // When not required, keep previous "no-type" behavior
            value: isLocationTypeRequired ? '' : 'no-type',
            rules: isLocationTypeRequired ? 'required|string|in:customer,delivery' : 'string',
            options: [
              { id: 'Customer', label: 'Customer', value: 'customer' },
              { id: 'Delivery', label: 'Delivery', value: 'delivery' },
            ],
          },
        ],
      },
    ];

    const hooks = {
      onSuccess: (data: Object) => {
        const { tradingAccount, company }: Object = data.values().addCompanyForm;

        const rawType: string = data.values().locationRequestFormType?.list || 'no-type';

        const locationType: ?string =
          rawType === 'customer' || rawType === 'delivery' ? rawType : null;

        // We reuse the same mode inferred in createForm (closed over in this scope)
        const isAssetRequest = Boolean(mode === 'asset' && locationType);
        const isRushRequest = Boolean(mode === 'rush' && locationType);

        // Base Payload
        const payload: any = {
          account_id: tradingAccount,
          name: company,
          activity_tag: 'add_company',
        };

        // userAddCompany(payload, isAssetRequest, locationType, isRushAssetRequest, rushType)
        this.props.actionDrawerStore.userAddCompany(
          payload,
          isAssetRequest,
          isAssetRequest ? locationType : null,
          isRushRequest,
          isRushRequest ? locationType : null
        );
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: { validateOnInit: true, validateOnChange: true },
        name: t('general:actions.new_company'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;

    if (actionDrawerStore.loading) return <Loading />;

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_company')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <AddNewCompanySuccess
          model={this.props.model}
          childmodel={this.props.childmodel}
          resetModels={this.props.resetModels}
          resetFields={this.props.resetFields}
        />
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_company')} />
        <AddNewCompanyForm form={this.createForm()} />
      </div>
    );
  }
}
